import { useCallback, useMemo, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { useUserProfile } from "@pontimed/auth-client";
import { cn } from "@pontimed/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertCircle, BellPlus, Building2, Check, Crown, Loader2, Minus, Plus } from "lucide-react";

import { CheckoutModal } from "@/components/billing/checkout";
import { formatPrice } from "@/components/billing/format-price";
import {
  baseOf,
  clinicQuote,
  hasPerSeatTiers,
  MAX_CLINIC_DOCTORS,
  seatTiersOf,
  type PerSeatTier,
} from "@/components/pricing/clinic-seat-pricing";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useBrowsingCountry } from "@/lib/hooks/use-browsing-country";

// The marketing pricing CTAs (apps/web /pricing and the /for-* landings) land
// here with the plan in the URL. The page confirms the plan, then pays in the
// same CheckoutModal as the rest of the portal — Stripe's embedded page,
// invoice details, saved cards, the PIN gate. Until 2026-10 it redirected to
// Stripe's hosted page and skipped the invoice details. Why the embedded page:
// _docs/stripe-checkout-ui.md.

type TargetType = "provider" | "facility" | "business" | "user";
type Interval = "monthly" | "annual";

interface SubscribeSearch {
  /** Plan code (e.g. provider_pro). Omitted → plan picker for `target`. */
  plan?: string;
  interval: Interval;
  /** Plan audience when no plan is selected yet. Marketing deep links may send
   *  it as `targetType` instead; both spellings land here. */
  target?: TargetType;
  /** Country code for PPP pricing (ISO-2, uppercase). */
  country?: string;
  /** Covered doctors for a seat-priced clinic plan (D2). Deep-linked by the
   *  /pricing clinic calculator so its number survives the domain hop. */
  doctors?: number;
  /** Which facility/business the purchase is for. Needed when the buyer holds a
   *  billing role at more than one — otherwise checkout answers
   *  400 `facility_required`. */
  entityId?: string;
}

interface PricingTierDto {
  name: string;
  planCode?: string;
  priceMonthly: number;
  priceAnnual: number;
  desc: string;
  features: string[];
  cta: string;
  highlight?: boolean;
  badge?: string;
  visibility?: "active" | "coming_soon";
  interestedCount?: number;
  /** D2 (`_docs/decizii-abonare-founding-2026-09-19.md` §2.5) — clinic plans are
   *  "base + rate per covered doctor". Optional: facility plans only, and only
   *  once the API side of the restructure has shipped. */
  basePriceMonthly?: number | null;
  perSeatTiers?: PerSeatTier[] | null;
}

interface TiersResponse {
  tiers: PricingTierDto[];
  country?: { code: string; pppMultiplier: number };
}

/** Subset of GET /api/account/workspaces (see components/layout/workspace-switcher). */
interface WorkspacesResponse {
  workspaces: { id: string; type: string; name: string; subtitle?: string; role?: string }[];
}

/** What the checkout was opened with. Frozen at "Continue to payment": a tiers
 *  refetch while the modal is open (a new waitlist count, say) must not reset
 *  the modal halfway through the payment. */
interface CheckoutConfig {
  plan: PricingTierDto;
  interval: Interval;
  quantity: number;
  entityId?: string;
}

const VALID_TARGETS: readonly TargetType[] = ["provider", "facility", "business", "user"] as const;

/** The facility roles that may buy for it — a copy of FACILITY_BILLING_ROLES
 *  (apps/api/src/lib/billing/resolve-entity.ts), used only to decide whether to
 *  ask which clinic; the API still decides who may pay. */
const FACILITY_BILLING_ROLES = ["owner", "director", "facility_manager", "admin", "billing"];

/** Covered-doctor bounds — MAX mirrors the server's `MAX_CLINIC_DOCTORS`. */
const MIN_DOCTORS = 1;
const MAX_DOCTORS = MAX_CLINIC_DOCTORS;

function clampDoctors(n: number): number {
  return Math.min(MAX_DOCTORS, Math.max(MIN_DOCTORS, Math.floor(n)));
}

/** Plan codes are audience-prefixed (seed: provider_*, facility_*, business_*, user_*). */
function targetFromPlan(plan: string | undefined): TargetType | undefined {
  if (!plan) return undefined;
  if (plan.startsWith("provider_") || plan.startsWith("doctor_")) return "provider";
  if (plan.startsWith("facility_") || plan.startsWith("clinic_")) return "facility";
  if (plan.startsWith("business_")) return "business";
  if (plan.startsWith("user_")) return "user";
  return undefined;
}

export const Route = createFileRoute("/_authenticated/billing/subscribe")({
  validateSearch: (search: Record<string, unknown>): SubscribeSearch => ({
    plan: typeof search.plan === "string" && search.plan ? search.plan : undefined,
    interval: search.interval === "annual" ? "annual" : "monthly",
    target: VALID_TARGETS.includes(search.target as TargetType)
      ? (search.target as TargetType)
      : VALID_TARGETS.includes(search.targetType as TargetType)
        ? (search.targetType as TargetType)
        : undefined,
    country:
      typeof search.country === "string" && /^[A-Za-z]{2}$/.test(search.country)
        ? search.country.toUpperCase()
        : undefined,
    doctors:
      Number.isFinite(Number(search.doctors)) && Number(search.doctors) > 0
        ? clampDoctors(Number(search.doctors))
        : undefined,
    entityId: typeof search.entityId === "string" && search.entityId ? search.entityId : undefined,
  }),
  component: SubscribePage,
});

function SubscribePage() {
  const {
    plan,
    interval,
    target,
    country: countryParam,
    doctors: doctorsParam,
    entityId: entityIdParam,
  } = Route.useSearch();
  const { country: cookieCountry } = useBrowsingCountry();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t, i18n } = useLingui();
  const { data: profile } = useUserProfile();

  const country = countryParam ?? (cookieCountry ? cookieCountry.toUpperCase() : undefined);
  const targetType: TargetType = targetFromPlan(plan) ?? target ?? "user";

  const tiersQuery = useQuery({
    queryKey: ["api", "/api/pricing/tiers", targetType, country ?? null, i18n.locale],
    queryFn: () =>
      apiFetch.get<TiersResponse>(
        `/api/pricing/tiers?targetType=${targetType}${country ? `&country=${encodeURIComponent(country)}` : ""}&locale=${i18n.locale}`,
      ),
  });

  const selected = useMemo(
    () => (plan ? tiersQuery.data?.tiers.find((tier) => tier.planCode === plan) : undefined),
    [plan, tiersQuery.data],
  );

  // D2: a seat-priced clinic plan is "base + flat rate × covered doctors", so the
  // checkout must carry `quantity` — without it the server charges base + 1 seat
  // and the /pricing calculator's number is unreachable from any clinic CTA.
  const seatPriced = !!selected && hasPerSeatTiers(selected);
  // Monthly-only is a property of the PLAN (decision 2), not of the entity: a
  // clinic buying `provider_*` coverage keeps the annual option. Before the
  // tiers load we fall back to the audience so a facility deep link does not
  // flash an annual toggle it will lose.
  const monthlyOnly = selected ? seatPriced : targetType === "facility";
  const effectiveInterval: Interval = monthlyOnly ? "monthly" : interval;
  const doctors = clampDoctors(doctorsParam ?? 1);
  const seatQuote = seatPriced && selected ? clinicQuote(selected, doctors) : null;

  const setDoctors = (next: number) =>
    navigate({
      to: "/billing/subscribe",
      search: {
        plan,
        interval,
        target,
        country: countryParam,
        entityId: entityIdParam,
        doctors: clampDoctors(next),
      },
      replace: true,
      resetScroll: false,
    });

  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  // H4: several facilities under one billing role → checkout answers
  // 400 `facility_required`; we then ask which one and resend with `entityId`.
  const [needsEntityPick, setNeedsEntityPick] = useState(false);
  // Starts empty so the URL param stays authoritative until the user picks.
  const [pickedEntityId, setPickedEntityId] = useState<string | undefined>(undefined);
  const effectiveEntityId = pickedEntityId ?? entityIdParam;

  const [checkoutConfig, setCheckoutConfig] = useState<CheckoutConfig | null>(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [resolvingEntity, setResolvingEntity] = useState(false);
  // Set once the purchase is paid; the page then confirms it instead of
  // offering the same plan again.
  const [purchased, setPurchased] = useState(false);
  // Stable: it ends up in the embedded Checkout's onComplete, which Stripe
  // reads once.
  const markPurchased = useCallback(() => setPurchased(true), []);
  const closeCheckout = useCallback(() => setCheckoutOpen(false), []);

  const workspacesQueryOptions = {
    queryKey: ["api", "/api/account/workspaces"],
    queryFn: () => apiFetch.get<WorkspacesResponse>("/api/account/workspaces"),
  };
  // The workspaces the plan can be bought for — the API's rule, so the picker
  // never offers a clinic it would refuse.
  const payableWorkspaces = (data: WorkspacesResponse | undefined) =>
    (data?.workspaces ?? []).filter(
      (w) =>
        w.type === targetType &&
        (targetType !== "facility" || FACILITY_BILLING_ROLES.includes(w.role ?? "")),
    );
  // Loaded only once we know the account must choose.
  const workspacesQuery = useQuery({ ...workspacesQueryOptions, enabled: needsEntityPick });
  const pickableWorkspaces = payableWorkspaces(workspacesQuery.data);

  const openCheckoutFor = (entityId: string | undefined) => {
    if (!selected) return;
    setCheckoutConfig({
      plan: selected,
      interval: effectiveInterval,
      quantity: seatPriced ? doctors : 1,
      entityId,
    });
    setCheckoutOpen(true);
  };

  // A clinic or business plan is bought FOR one of them. The API picks it when
  // the account can pay for exactly one; for several it must be told which
  // (400 `facility_required` / `business_required`). Asked here, before the
  // payment steps, rather than as an error at their end.
  const startCheckout = async () => {
    setCheckoutError(null);
    if ((targetType !== "facility" && targetType !== "business") || effectiveEntityId) {
      openCheckoutFor(effectiveEntityId);
      return;
    }
    setResolvingEntity(true);
    try {
      const candidates = payableWorkspaces(await queryClient.fetchQuery(workspacesQueryOptions));
      if (candidates.length === 0) {
        setCheckoutError(
          targetType === "facility"
            ? t`You do not manage a clinic yet.`
            : t`You do not manage a business yet.`,
        );
      } else if (candidates.length === 1) {
        // Named, not left to the API: it counts pending invitations and
        // removed clinics too, and would ask "which one?" at the pay step.
        openCheckoutFor(candidates[0]!.id);
      } else {
        setNeedsEntityPick(true);
      }
    } catch {
      setCheckoutError(t`We could not load the list. Please try again.`);
    } finally {
      setResolvingEntity(false);
    }
  };

  const [waitlistJoined, setWaitlistJoined] = useState(false);
  const waitlist = useMutation({
    mutationFn: () =>
      apiFetch.post("/api/plan-waitlist", {
        planCode: plan,
        countryCode: country,
        name: [profile?.firstName, profile?.lastName].filter(Boolean).join(" ") || profile?.email,
        email: profile?.email,
        locale: i18n.locale,
      }),
    onSuccess: () => setWaitlistJoined(true),
  });

  const setIntervalChoice = (next: Interval) =>
    navigate({
      to: "/billing/subscribe",
      search: (prev) => ({ ...prev, interval: next }),
      replace: true,
    });

  const selectPlan = (tier: PricingTierDto) => {
    if (!tier.planCode) return;
    // Free tiers (plan price 0 — `*_free`, `facility_starter`, …) have nothing to
    // pay for: never send them into the paid checkout.
    if (tier.priceMonthly === 0) {
      navigate({ to: "/account/user/dashboard" });
      return;
    }
    navigate({
      to: "/billing/subscribe",
      search: {
        plan: tier.planCode,
        interval,
        target,
        country: countryParam,
        doctors: hasPerSeatTiers(tier) ? doctors : undefined,
        entityId: entityIdParam,
      },
    });
  };

  return (
    <div className="mx-auto max-w-2xl pb-10">
      {purchased && !checkoutOpen ? (
        <div className="border-success-border bg-success-soft rounded-xl border p-6 text-center">
          <div className="bg-success text-success-foreground mx-auto flex size-12 items-center justify-center rounded-full">
            <Check className="size-6" />
          </div>
          <p className="text-success-strong mt-3 text-base font-semibold">
            <Trans>Payment confirmed — your subscription is active.</Trans>
          </p>
          <Button
            className="mt-5 min-h-[44px] w-full sm:w-auto"
            onClick={() =>
              navigate({ to: "/account/user/billing", search: { tab: "subscription" } })
            }
          >
            <Trans>Explore your account</Trans>
          </Button>
        </div>
      ) : (
        <>
          <header className="mb-6">
            <h1 className="font-display text-foreground text-2xl font-semibold tracking-[-0.02em]">
              {selected ? <Trans>Confirm your plan</Trans> : <Trans>Choose a plan</Trans>}
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">
              <Trans>Secure payment via Stripe. Cancel anytime.</Trans>
            </p>
          </header>

          {/* Interval toggle — hidden for clinic plans (monthly only, decision 2) */}
          {!monthlyOnly && (
            <div className="bg-muted mb-6 flex rounded-lg p-1" role="tablist">
              {(["monthly", "annual"] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  role="tab"
                  aria-selected={interval === option}
                  onClick={() => setIntervalChoice(option)}
                  className={cn(
                    "min-h-[44px] flex-1 rounded-md text-sm font-medium transition-colors",
                    interval === option
                      ? "bg-card text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {option === "monthly" ? (
                    <Trans>Monthly</Trans>
                  ) : (
                    <span className="inline-flex items-center gap-1.5">
                      <Trans>Annual</Trans>
                      <span className="bg-success-soft text-success-strong rounded-full px-1.5 py-0.5 text-[0.625rem] font-semibold">
                        <Trans>2 months free</Trans>
                      </span>
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {tiersQuery.isLoading && (
            <div className="space-y-3">
              <Skeleton className="h-44 w-full rounded-xl" />
              <Skeleton className="h-44 w-full rounded-xl" />
            </div>
          )}

          {tiersQuery.isError && (
            <div className="border-destructive/40 bg-destructive/5 rounded-xl border p-6 text-center">
              <p className="text-destructive text-sm font-medium">
                <Trans>Could not load plans.</Trans>
              </p>
              <Button
                size="sm"
                variant="outline"
                className="mt-4"
                onClick={() => tiersQuery.refetch()}
              >
                <Trans>Retry</Trans>
              </Button>
            </div>
          )}

          {/* D2 covered-doctor count — the number the clinic is billed for. Mirrors
              the /pricing calculator, which deep-links its own count as ?doctors=. */}
          {seatPriced && seatQuote && (
            <div className="border-border bg-card mb-4 rounded-xl border p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-foreground text-sm font-semibold">
                    <Trans>Covered doctors</Trans>
                  </p>
                  <p className="text-muted-foreground mt-0.5 text-xs">
                    <Trans>Doctors covered by the clinic pay nothing.</Trans>
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    aria-label={t`Decrease the number of doctors`}
                    onClick={() => setDoctors(doctors - 1)}
                    disabled={doctors <= MIN_DOCTORS}
                    className="bg-muted hover:bg-muted/70 text-foreground flex size-11 items-center justify-center rounded-full transition-colors active:scale-95 disabled:opacity-30"
                  >
                    <Minus className="size-4" />
                  </button>
                  <span className="w-10 text-center text-lg font-bold tabular-nums">{doctors}</span>
                  <button
                    type="button"
                    aria-label={t`Increase the number of doctors`}
                    onClick={() => setDoctors(doctors + 1)}
                    disabled={doctors >= MAX_DOCTORS}
                    className="bg-muted hover:bg-muted/70 text-foreground flex size-11 items-center justify-center rounded-full transition-colors active:scale-95 disabled:opacity-30"
                  >
                    <Plus className="size-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* H4: the account can pay for several facilities (or businesses) —
              checkout can't guess which one this plan is for. */}
          {needsEntityPick && (
            <div className="border-warning/30 bg-warning-soft mb-4 rounded-xl border p-4">
              <p className="text-warning-strong text-sm font-semibold">
                <Trans>Which one is this subscription for?</Trans>
              </p>
              {workspacesQuery.isLoading ? (
                <div className="mt-3 space-y-2">
                  <Skeleton className="h-11 w-full rounded-lg" />
                  <Skeleton className="h-11 w-full rounded-lg" />
                </div>
              ) : pickableWorkspaces.length === 0 ? (
                <p className="text-muted-foreground mt-2 text-xs">
                  <Trans>We could not load the list. Please try again.</Trans>
                </p>
              ) : (
                <div className="mt-3 space-y-2" role="radiogroup">
                  {pickableWorkspaces.map((w) => (
                    <button
                      key={w.id}
                      type="button"
                      role="radio"
                      aria-checked={effectiveEntityId === w.id}
                      onClick={() => {
                        setPickedEntityId(w.id);
                        setCheckoutError(null);
                      }}
                      className={cn(
                        "flex min-h-[44px] w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors",
                        effectiveEntityId === w.id
                          ? "border-primary bg-primary-soft"
                          : "border-border bg-card hover:bg-muted",
                      )}
                    >
                      <Building2 className="text-muted-foreground size-4 shrink-0" />
                      <span className="min-w-0 flex-1">
                        <span className="text-foreground block truncate text-sm font-medium">
                          {w.name}
                        </span>
                        {w.subtitle && (
                          <span className="text-muted-foreground block truncate text-xs">
                            {w.subtitle}
                          </span>
                        )}
                      </span>
                      {effectiveEntityId === w.id && (
                        <Check className="text-primary size-4 shrink-0" />
                      )}
                    </button>
                  ))}
                  <Button
                    className="min-h-[44px] w-full"
                    disabled={!effectiveEntityId}
                    onClick={() => {
                      setCheckoutError(null);
                      openCheckoutFor(effectiveEntityId);
                    }}
                  >
                    <Trans>Continue to payment</Trans>
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Plan summary (plan param present and resolved) */}
          {selected && (
            <PlanSummaryCard
              tier={selected}
              interval={effectiveInterval}
              doctors={seatPriced ? doctors : undefined}
              waitlistJoined={waitlistJoined}
              onSubscribe={() => void startCheckout()}
              onJoinWaitlist={() => waitlist.mutate()}
              subscribing={resolvingEntity}
              joiningWaitlist={waitlist.isPending}
              error={checkoutError ?? (waitlist.isError ? t`Could not join the waitlist.` : null)}
              onChangePlan={() =>
                navigate({
                  to: "/billing/subscribe",
                  search: { interval, target: targetType, country: countryParam },
                })
              }
            />
          )}

          {/* Unknown plan code */}
          {plan && tiersQuery.isSuccess && !selected && (
            <div className="border-border bg-card mb-4 rounded-xl border p-6 text-center">
              <p className="text-foreground text-sm font-semibold">
                <Trans>This plan is not available.</Trans>
              </p>
              <p className="text-muted-foreground mt-1 text-xs">
                <Trans>Pick one of the plans below.</Trans>
              </p>
            </div>
          )}

          {/* Plan picker */}
          {!selected && tiersQuery.isSuccess && (
            <div className="space-y-3">
              {tiersQuery.data.tiers
                .filter((tier) => tier.planCode)
                .map((tier) => (
                  <PlanPickerCard
                    key={tier.planCode}
                    tier={tier}
                    interval={effectiveInterval}
                    onSelect={() => selectPlan(tier)}
                  />
                ))}
            </div>
          )}
        </>
      )}

      {/* Mounted once opened (not only while open) so the sheet animates out. */}
      {checkoutConfig && (
        <CheckoutModal
          open={checkoutOpen}
          onClose={closeCheckout}
          onPaymentComplete={markPurchased}
          planCode={checkoutConfig.plan.planCode}
          planData={checkoutConfig.plan}
          entityType={targetType}
          entityId={checkoutConfig.entityId}
          initialInterval={checkoutConfig.interval}
          initialQuantity={checkoutConfig.quantity}
        />
      )}
    </div>
  );
}

/** "{total}/month for {n} doctors · base {base} + {n} × {seat}" (D2). */
function SeatTotalBreakdownMsg({
  count,
  base,
  seat,
}: {
  count: number;
  base: string;
  seat: string;
}) {
  return (
    // prettier-ignore
    <Trans>for {count} doctors · base {base} + {count} × {seat}</Trans>
  );
}

/** D3: the subscribe funnel prices in € (like /pricing), in the UI locale's format. */
const FUNNEL_CURRENCY = "EUR";

/**
 * "from {base} + {perSeat}/doctor" — the D2 clinic headline for cards where no
 * doctor count is known yet (the plan picker). Renders nothing for plans the API
 * has not marked as seat-priced, so provider / business / user plans are
 * untouched.
 */
function SeatPriceLine({ tier, className }: { tier: PricingTierDto; className?: string }) {
  const { i18n } = useLingui();
  if (!hasPerSeatTiers(tier)) return null;
  const brackets = seatTiersOf(tier) ?? [];
  const first = brackets[0];
  if (!first) return null;
  const base = formatPrice(baseOf(tier), FUNNEL_CURRENCY, i18n.locale);
  const perSeat = formatPrice(first.pricePerSeat, FUNNEL_CURRENCY, i18n.locale);
  return (
    <p className={className ?? "text-primary mt-1 text-sm font-semibold"}>
      {/* prettier-ignore */}
      <Trans>from {base} + {perSeat}/doctor</Trans>
    </p>
  );
}

function PlanSummaryCard({
  tier,
  interval,
  doctors,
  onSubscribe,
  onJoinWaitlist,
  onChangePlan,
  subscribing,
  joiningWaitlist,
  waitlistJoined,
  error,
}: {
  tier: PricingTierDto;
  interval: Interval;
  /** Covered doctors — set only for a seat-priced clinic plan. */
  doctors?: number;
  onSubscribe: () => void;
  onJoinWaitlist: () => void;
  onChangePlan: () => void;
  subscribing: boolean;
  joiningWaitlist: boolean;
  waitlistJoined: boolean;
  error: string | null;
}) {
  const { i18n } = useLingui();
  const isComingSoon = tier.visibility === "coming_soon";
  const seatPriced = hasPerSeatTiers(tier);
  // D2: the headline on a clinic plan is the amount that will actually be
  // charged for `doctors` covered doctors — not the bare base price.
  const quote = seatPriced && doctors ? clinicQuote(tier, doctors) : null;
  const price = quote
    ? quote.monthly
    : seatPriced
      ? baseOf(tier)
      : interval === "annual"
        ? tier.priceAnnual
        : tier.priceMonthly;

  return (
    <div className="from-primary/15 to-accent/15 border-primary/20 rounded-xl border bg-gradient-to-br p-6">
      <div className="flex items-start gap-3">
        <div className="bg-primary text-primary-foreground flex size-12 shrink-0 items-center justify-center rounded-xl">
          <Crown className="size-6" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-foreground text-lg font-bold">{tier.name}</p>
          <p className="text-foreground text-3xl font-bold tracking-tight">
            {formatPrice(price, FUNNEL_CURRENCY, i18n.locale)}
            <span className="text-muted-foreground text-sm font-normal">
              {interval === "annual" && !seatPriced ? <Trans>/year</Trans> : <Trans>/month</Trans>}
            </span>
          </p>
          {quote ? (
            <p className="text-primary mt-1 text-sm font-semibold">
              <SeatTotalBreakdownMsg
                count={quote.doctors}
                base={formatPrice(quote.basePriceMonthly, FUNNEL_CURRENCY, i18n.locale)}
                seat={formatPrice(quote.perSeatPrice, FUNNEL_CURRENCY, i18n.locale)}
              />
            </p>
          ) : (
            <SeatPriceLine tier={tier} />
          )}
          {tier.desc && <p className="text-muted-foreground mt-1 text-sm">{tier.desc}</p>}
        </div>
      </div>

      {tier.features.length > 0 && (
        <ul className="mt-4 space-y-1.5">
          {tier.features.slice(0, 6).map((feature) => (
            <li key={feature} className="text-foreground flex items-start gap-2 text-sm">
              <Check className="text-success mt-0.5 size-4 shrink-0" />
              {feature}
            </li>
          ))}
        </ul>
      )}

      {error && (
        <div className="border-destructive-border bg-destructive-soft text-destructive-strong mt-4 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
          <AlertCircle className="size-4 shrink-0" />
          {error}
        </div>
      )}

      <div className="mt-6 space-y-2">
        {isComingSoon ? (
          waitlistJoined ? (
            <div className="bg-success-soft text-success-strong flex min-h-[44px] items-center justify-center gap-2 rounded-lg text-sm font-semibold">
              <Check className="size-4" />
              <Trans>You're on the waitlist — we'll email you at launch.</Trans>
            </div>
          ) : (
            <Button
              className="min-h-[44px] w-full gap-1.5 text-sm font-semibold"
              onClick={onJoinWaitlist}
              disabled={joiningWaitlist}
            >
              {joiningWaitlist ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <BellPlus className="size-4" />
              )}
              <Trans>Join the waitlist</Trans>
            </Button>
          )
        ) : (
          <Button
            className="min-h-[44px] w-full gap-1.5 text-sm font-semibold"
            onClick={onSubscribe}
            disabled={subscribing}
          >
            {subscribing && <Loader2 className="size-4 animate-spin" />}
            <Trans>Continue to payment</Trans>
          </Button>
        )}
        <Button variant="ghost" className="min-h-[44px] w-full text-sm" onClick={onChangePlan}>
          <Trans>Choose a different plan</Trans>
        </Button>
      </div>
    </div>
  );
}

function PlanPickerCard({
  tier,
  interval,
  onSelect,
}: {
  tier: PricingTierDto;
  interval: Interval;
  onSelect: () => void;
}) {
  const { i18n } = useLingui();
  const seatPriced = hasPerSeatTiers(tier);
  const price = seatPriced
    ? baseOf(tier)
    : interval === "annual"
      ? tier.priceAnnual
      : tier.priceMonthly;
  const isFree = tier.priceMonthly === 0;
  const isComingSoon = tier.visibility === "coming_soon";

  return (
    <Card className={cn(tier.highlight && "border-primary/40")}>
      <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-foreground text-base font-semibold">{tier.name}</p>
            {tier.badge && (
              <span className="bg-accent-soft text-accent-strong rounded-full px-2 py-0.5 text-[0.625rem] font-semibold tracking-wide uppercase">
                {tier.badge}
              </span>
            )}
            {isComingSoon && (
              <span className="bg-warning-soft text-warning-strong rounded-full px-2 py-0.5 text-[0.625rem] font-semibold tracking-wide uppercase">
                <Trans>Coming soon</Trans>
              </span>
            )}
          </div>
          <p className="text-foreground mt-1 text-xl font-bold">
            {isFree ? (
              <Trans>Free</Trans>
            ) : (
              <>
                {formatPrice(price, FUNNEL_CURRENCY, i18n.locale)}
                <span className="text-muted-foreground text-xs font-normal">
                  {interval === "annual" && !seatPriced ? (
                    <Trans>/year</Trans>
                  ) : (
                    <Trans>/month</Trans>
                  )}
                </span>
              </>
            )}
          </p>
          <SeatPriceLine tier={tier} className="text-primary text-xs font-semibold" />
          {tier.desc && (
            <p className="text-muted-foreground mt-1 line-clamp-2 text-xs">{tier.desc}</p>
          )}
        </div>
        <Button
          variant={tier.highlight ? "default" : "outline"}
          className="min-h-[44px] shrink-0 sm:min-w-28"
          onClick={onSelect}
        >
          {isFree ? <Trans>Get started</Trans> : <Trans>Select</Trans>}
        </Button>
      </CardContent>
    </Card>
  );
}
